'use client';

import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { bgOrange } from '@osmosis/tailwind-ui/styles/background';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { borderNeutral200 } from '@osmosis/tailwind-ui/styles/border';

// Dismissing the banner hides it for a day, regardless of which page it was
// dismissed on (acknowledges the user has read it for the current session).
const DISMISS_STORAGE_KEY = 'oai-trust-strip-dismissed-until';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

type Props = {
  classNames?: {
    root?: string;
    text?: string;
  };
  iconSize?: number;
};

/**
 * TrustStrip.
 */
export const TrustStrip = ({ classNames = {}, iconSize = 12 }: Props) => {
  const { formatMessage } = useIntl();
  const colors = useColors();

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(DISMISS_STORAGE_KEY));
      if (until && Date.now() < until) setDismissed(true);
    } catch {
      // localStorage unavailable (SSR / privacy mode) — show the banner.
    }
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_STORAGE_KEY,
        String(Date.now() + DISMISS_DURATION_MS),
      );
    } catch {
      // Ignore persistence failures; still hide for this session.
    }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <Flex
      data-testid="oai-trust-strip"
      className={cn(
        bgOrange,
        'w-full items-center justify-between px-4',
        classNames.root,
      )}
    >
      <Flex className={cn('min-w-0 items-center gap-2')}>
        <Icon
          icon={faShieldCheck}
          size={iconSize}
          className={cn(text0, 'shrink-0')}
        />
        <P
          // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
          className={cn('!text-[10px] py-1', text0, classNames.text)}
          style={{ lineHeight: '1rem' }}
        >
          {formatMessage({
            id: 'ai-panel.trust-prefix',
            defaultMessage: 'Grounded in ',
          })}
          <span className={cn(text0, 'mr-1')}>
            {formatMessage({
              id: 'ai-panel.trust-source',
              defaultMessage: 'trusted Elsevier sources',
            })}
          </span>
          <span className="inline-flex items-center">
            <span>
              {formatMessage({
                id: 'ai-panel.trust-suffix',
                defaultMessage: '-- not the open web',
              })}
            </span>
            <IconButton
              variant="transparent"
              round
              icon={<Icon icon={faCircleInfo} size={10} />}
              ariaLabel={formatMessage({
                id: 'ai-panel.trust-info-label',
                defaultMessage: 'About Osmosis AI sources tooltip',
              })}
              className={cn(text0, 'h-4 w-4')}
              data-tooltip-id="oai-trust-info-tooltip"
              data-testid="oai-trust-info-btn"
            />
          </span>
        </P>
      </Flex>

      <Button
        variant="transparent"
        color="transparent"
        startIcon={<Icon icon={faXmark} size={10} className={text0} />}
        onClick={handleDismiss}
        // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
        className={cn(text0, 'shrink-0 whitespace-nowrap !px-2 text-[8px]')}
        data-testid="oai-trust-dismiss-btn"
      >
        {formatMessage({
          id: 'ai-panel.trust-dismiss',
          defaultMessage: 'Dismiss',
        })}
      </Button>

      <Tooltip
        id="oai-trust-info-tooltip"
        opacity={1}
        style={{
          backgroundColor: '#303030',
          borderRadius: 5,
          color: colors.textInvert,
          zIndex: ZIndex.MODAL,
          maxWidth: 350,
        }}
      >
        <Flex className="flex-col gap-3">
          <P color="inherit" className="!text-base !font-bold">
            {formatMessage({
              id: 'ai-panel.trust-tooltip.title',
              defaultMessage: 'Why you can trust Osmosis AI',
            })}
          </P>
          <P color="inherit" className="!text-sm">
            {formatMessage({
              id: 'ai-panel.trust-tooltip.sources',
              defaultMessage:
                'Osmosis AI retrieves from trusted Elsevier and Osmosis medical learning sources, not open-web search.',
            })}
          </P>
          <P color="inherit" className="!text-sm">
            {formatMessage({
              id: 'ai-panel.trust-tooltip.examples',
              defaultMessage:
                "Examples may include Gray's Anatomy for Students, Robbins & Kumar Basic Pathology, and Guyton and Hall.",
            })}
          </P>
          <P color="inherit" className="!text-sm">
            {formatMessage({
              id: 'ai-panel.trust-tooltip.citations',
              defaultMessage:
                'Actual sources vary by question and may appear as citations in responses.',
            })}
          </P>
          <Box className={`border-t pt-3 ${borderNeutral200}`}>
            <P color="inherit" className="!text-sm">
              {formatMessage({
                id: 'ai-panel.trust-tooltip.disclaimer',
                defaultMessage:
                  'For learning support only — not for clinical decision-making or patient care.',
              })}
            </P>
          </Box>
        </Flex>
      </Tooltip>
    </Flex>
  );
};
